import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Building, Home, Wrench, TrendingUp, 
  DollarSign, Sparkles, Calendar, LayoutGrid 
} from 'lucide-react';
import './RoomManagementModule.css';

// Import individual tab components
import RoomInventoryTab from './tabs/RoomInventoryTab';
import HousekeepingTab from './tabs/HousekeepingTab';
import MaintenanceTab from './tabs/MaintenanceTab';
import PricingTab from './tabs/PricingTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import AvailabilityTab from './tabs/AvailabilityTab';

const RoomManagementModule = () => {
  const { token, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('inventory');
  const [hotelId, setHotelId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Extract hotel_id from userProfile data - try multiple possible locations
    if (userProfile) {
      const extractedHotelId = userProfile.hotel_id || 
                               userProfile.hotelId || 
                               userProfile.managed_hotel_id ||
                               userProfile.managedHotels?.[0]?.hotel_id || 
                               null;
      
      console.log('[RoomManagementModule] userProfile:', userProfile);
      console.log('[RoomManagementModule] Extracted hotel_id:', extractedHotelId);
      console.log('[RoomManagementModule] token exists:', !!token);
      
      setHotelId(extractedHotelId);
      setLoading(false);
    }
  }, [userProfile, token]);

  const tabs = [
    {
      id: 'inventory',
      label: 'Room Inventory',
      icon: LayoutGrid,
      component: RoomInventoryTab,
      description: 'Manage rooms, types, and configurations'
    },
    {
      id: 'services',
      label: 'Services',
      icon: Sparkles,
      component: HousekeepingTab,
      description: 'Track guest service requests by category'
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: Wrench,
      component: MaintenanceTab,
      description: 'Manage repair issues and technicians'
    },
    {
      id: 'pricing',
      label: 'Pricing',
      icon: DollarSign,
      component: PricingTab,
      description: 'Dynamic pricing and rate management'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: TrendingUp,
      component: AnalyticsTab,
      description: 'Occupancy, revenue, and performance metrics'
    },
    {
      id: 'availability',
      label: 'Availability',
      icon: Calendar,
      component: AvailabilityTab,
      description: 'Calendar view and booking status'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // Show loading if still determining hotelId
  if (loading) {
    return (
      <div className="room-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading hotel information...</p>
      </div>
    );
  }

  // Show error if no hotelId found
  if (!hotelId) {
    return (
      <div className="room-management-module" style={{padding: '40px'}}>
        <div style={{textAlign: 'center', maxWidth: '600px', margin: '0 auto'}}>
          <h3 style={{color: '#ef4444', marginBottom: '16px'}}>Hotel ID Not Found</h3>
          <p style={{marginBottom: '20px'}}>Unable to determine your hotel. Please ensure you are assigned to a hotel.</p>
          <details style={{textAlign: 'left', background: '#f9fafb', padding: '16px', borderRadius: '8px', marginTop: '20px'}}>
            <summary style={{cursor: 'pointer', fontWeight: 600, marginBottom: '12px'}}>Debug: User Data</summary>
            <pre style={{fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap'}}>
              {JSON.stringify(userProfile, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="room-management-module">
      {/* Header with Module Title */}
      <div className="module-header">
        <div className="module-title">
          <Building className="module-icon" size={32} />
          <div>
            <h1>Room Management System</h1>
            <p className="module-subtitle">Comprehensive room operations and control center</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tabs-container">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                title={tab.description}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Tab Content */}
      <div className="tab-content">
        {ActiveComponent && (
          <ActiveComponent 
            hotelId={hotelId} 
            token={token}
            user={userProfile}
          />
        )}
      </div>
    </div>
  );
};

export default RoomManagementModule;
